import React, { useState } from 'react';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { createRecord, listManufacturers, listModels, listRecords, toApiError, updateRecord } from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import { usePocAccess } from '../../components/poc/PocAccess';
import { LifecycleActions, LifecycleTimeline } from '../../components/poc/RecordLifecycle';
import {
    ADMIN_LINKS, AccessDenied, DetailList, Disclaimer, EmptyState, ErrorNotice, Field, LoadingState, PageHeader, Panel,
    StatusBadge, SubNav,
} from '../../components/poc/PocUi';
import {
    COVERAGE_TYPES, DISTANCE_UNITS, EXCLUSION_MATCH_TYPES, LIMIT_RULES, PATTERNS, SOURCE_TYPES, formatNumber, isRealDate,
    manufacturerName, splitCodes,
} from '../../components/poc/pocFormat';

const STATUSES = ['DRAFT', 'ACTIVE', 'RETIRED'];
const EMPTY_EXCLUSION = { match_type: 'PART_CODE', match_value: '', reason_code: '', description: '' };
const EMPTY_POLICY = {
    policy_id: '', policy_name: '', coverage_type: 'BASIC', manufacturer_code: '', model_code: '', market_code: '',
    model_year_from: '', model_year_to: '', duration_months: '', distance_limit: '', distance_unit: 'KM',
    limit_rule: 'WHICHEVER_FIRST', priority: '100', effective_from: '', effective_to: '', source_type: 'ICMS_DEMO_DEFAULT',
    source_reference: '', disclaimer: '', coverage: '', exclusions: [],
};

const str = (v) => (v === null || v === undefined ? '' : String(v));

/** Form state from a stored policy (to edit a draft, or to start a new draft from any policy). */
export function policyToForm(policy, { keepId }) {
    return {
        ...EMPTY_POLICY,
        ...Object.fromEntries(Object.keys(EMPTY_POLICY).filter((k) => !['coverage', 'exclusions'].includes(k)).map((k) => [k, str(policy[k])])),
        policy_id: keepId ? policy.policy_id : '',
        coverage: (policy.coverage || []).join(', '),
        exclusions: (policy.exclusions || []).map(({ match_type, match_value, reason_code, description }) => ({ match_type, match_value, reason_code, description })),
    };
}

function intField(errors, form, field, min, max, label) {
    const text = str(form[field]).trim();
    if (!text) return null;
    if (!/^\d+$/.test(text) || Number(text) < min || Number(text) > max) {
        errors[field] = `${label} must be a whole number from ${formatNumber(min)} to ${formatNumber(max)}.`;
        return null;
    }
    return Number(text);
}

/** PolicyCreateIn / PolicyConfigIn body. Status and timestamps are server-owned and never sent. */
export function buildPolicyBody(form, { includeId }) {
    const errors = {};
    const t = (k) => str(form[k]).trim();
    const orNull = (k) => t(k) || null;
    const body = {};
    if (includeId) {
        body.policy_id = t('policy_id').toUpperCase();
        if (!PATTERNS.recordId.test(body.policy_id)) errors.policy_id = '3-64 upper-case letters, digits, "_", "." or "-".';
    }
    body.policy_name = t('policy_name');
    if (!body.policy_name || body.policy_name.length > 200) errors.policy_name = 'Enter a name (up to 200 characters).';
    body.coverage_type = form.coverage_type;
    body.manufacturer_code = orNull('manufacturer_code');
    body.model_code = orNull('model_code');
    body.market_code = orNull('market_code');
    if (body.market_code && !PATTERNS.market.test(body.market_code)) errors.market_code = 'Upper-case code, up to 16 characters.';
    body.model_year_from = intField(errors, form, 'model_year_from', 1900, 2100, 'Model year');
    body.model_year_to = intField(errors, form, 'model_year_to', 1900, 2100, 'Model year');
    body.duration_months = intField(errors, form, 'duration_months', 1, 1200, 'Duration');
    body.distance_limit = intField(errors, form, 'distance_limit', 1, 10000000, 'Distance limit');
    body.distance_unit = form.distance_unit;
    body.limit_rule = form.limit_rule;
    body.priority = t('priority') ? intField(errors, form, 'priority', 0, 1000000, 'Priority') : 100;
    body.effective_from = t('effective_from');
    if (!isRealDate(body.effective_from)) errors.effective_from = 'Enter a valid start date.';
    body.effective_to = orNull('effective_to');
    if (body.effective_to && !isRealDate(body.effective_to)) errors.effective_to = 'Enter a valid end date.';
    body.source_type = form.source_type;
    body.source_reference = orNull('source_reference');
    body.disclaimer = orNull('disclaimer');
    body.coverage = splitCodes(form.coverage).map((c) => c.toUpperCase());
    if (body.coverage.some((c) => !PATTERNS.category.test(c))) errors.coverage = 'Upper-case category codes such as ENGINE, separated by commas.';
    body.exclusions = form.exclusions.map((e) => ({
        match_type: e.match_type, match_value: str(e.match_value).trim(), reason_code: str(e.reason_code).trim().toUpperCase(),
        description: str(e.description).trim(),
    }));
    if (body.exclusions.some((e) => !PATTERNS.code.test(e.match_value) || !PATTERNS.reasonCode.test(e.reason_code) || !e.description)) {
        errors.exclusions = 'Each exclusion needs a value (letters, digits, "_", "." or "-"), a reason code (2-16 upper-case characters) and a description.';
    }
    return { body, errors };
}

function PolicyForm({ mode, initial, manufacturers, onSaved, onCancel }) {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const models = useApiResource(
        (signal) => (form.manufacturer_code ? listModels(form.manufacturer_code, signal) : Promise.resolve([])),
        [form.manufacturer_code],
    );
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setExclusion = (i, k) => (e) => setForm((f) => ({
        ...f, exclusions: f.exclusions.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)),
    }));

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        const { body, errors: found } = buildPolicyBody(form, { includeId: mode === 'create' });
        setErrors(found);
        setError(null);
        if (Object.keys(found).length) return;
        setSaving(true);
        try {
            const saved = mode === 'create' ? await createRecord('policy', body) : await updateRecord('policy', initial.policy_id, body);
            onSaved(saved);
        } catch (err) {
            const apiError = toApiError(err);
            setErrors((prev) => ({ ...prev, ...apiError.fieldErrors }));
            setError(apiError);
        } finally {
            setSaving(false);
        }
    };

    const input = (k, props = {}) => <input type="text" className="form-input" value={form[k]} onChange={set(k)} {...props} />;
    const select = (k, options, extra = null) => (
        <select className="form-select" value={form[k]} onChange={set(k)}>
            {extra}
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );

    return (
        <Panel title={mode === 'create' ? 'New Draft Policy' : `Edit Draft ${initial.policy_id}`}>
            <form onSubmit={save} noValidate className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {mode === 'create' && (
                        <Field label="Policy ID" required error={errors.policy_id} hint="A new ID; active policy content is never edited in place.">
                            {input('policy_id', { maxLength: 64 })}
                        </Field>
                    )}
                    <Field label="Policy Name" required error={errors.policy_name} className="md:col-span-2">{input('policy_name', { maxLength: 200 })}</Field>
                    <Field label="Coverage Type">{select('coverage_type', COVERAGE_TYPES)}</Field>
                    <Field label="Manufacturer" hint="Empty applies to every manufacturer.">
                        <select className="form-select" value={form.manufacturer_code}
                                onChange={(e) => setForm((f) => ({ ...f, manufacturer_code: e.target.value, model_code: '' }))}>
                            <option value="">All manufacturers</option>
                            {manufacturers.map((m) => <option key={m.manufacturer_code} value={m.manufacturer_code}>{m.manufacturer_name} ({m.manufacturer_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Model" hint={form.manufacturer_code && !models.loading && !(models.data || []).length ? 'No models are configured for this manufacturer.' : 'Empty applies to every model of the manufacturer.'}>
                        <select className="form-select" value={form.model_code} onChange={set('model_code')} disabled={!form.manufacturer_code}>
                            <option value="">All models</option>
                            {form.model_code && !(models.data || []).some((m) => m.model_code === form.model_code) && <option value={form.model_code}>{form.model_code}</option>}
                            {(models.data || []).map((m) => <option key={m.model_code} value={m.model_code}>{(m.model_name || '').trim() || m.model_code} ({m.model_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Market Code" error={errors.market_code}>{input('market_code', { maxLength: 16 })}</Field>
                    <Field label="Model Year From" error={errors.model_year_from}>{input('model_year_from', { inputMode: 'numeric' })}</Field>
                    <Field label="Model Year To" error={errors.model_year_to}>{input('model_year_to', { inputMode: 'numeric' })}</Field>
                    <Field label="Duration (months)" error={errors.duration_months}>{input('duration_months', { inputMode: 'numeric' })}</Field>
                    <Field label="Distance Limit" error={errors.distance_limit}>{input('distance_limit', { inputMode: 'numeric' })}</Field>
                    <Field label="Distance Unit">{select('distance_unit', DISTANCE_UNITS)}</Field>
                    <Field label="Limit Rule">{select('limit_rule', LIMIT_RULES)}</Field>
                    <Field label="Priority" error={errors.priority} hint="Among equally specific policies, the lower number wins.">{input('priority', { inputMode: 'numeric' })}</Field>
                    <Field label="Effective From" required error={errors.effective_from}><input type="date" className="form-input" value={form.effective_from} onChange={set('effective_from')} /></Field>
                    <Field label="Effective To" error={errors.effective_to}><input type="date" className="form-input" value={form.effective_to} onChange={set('effective_to')} /></Field>
                    <Field label="Source Type" hint="Customer and OEM sources need a source reference.">{select('source_type', SOURCE_TYPES)}</Field>
                    <Field label="Source Reference" className="md:col-span-2">{input('source_reference', { maxLength: 500 })}</Field>
                    <Field label="Covered Component Categories" error={errors.coverage} hint="Comma-separated, e.g. ENGINE, TRANSMISSION." className="md:col-span-3">
                        {input('coverage')}
                    </Field>
                    <Field label="Disclaimer" hint="Optional; the database default disclaimer is used when empty." className="md:col-span-3">
                        <textarea rows={2} value={form.disclaimer} onChange={set('disclaimer')} maxLength={500} className="form-input" />
                    </Field>
                </div>

                <fieldset className="space-y-3">
                    <legend className="text-xs font-semibold text-text-muted uppercase tracking-widest">Exclusions</legend>
                    {form.exclusions.map((x, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <Field label={`Match type ${i + 1}`} className="md:col-span-2">
                                <select className="form-select" value={x.match_type} onChange={setExclusion(i, 'match_type')}>
                                    {EXCLUSION_MATCH_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </Field>
                            <Field label={`Value ${i + 1}`} className="md:col-span-3"><input type="text" className="form-input" value={x.match_value} onChange={setExclusion(i, 'match_value')} /></Field>
                            <Field label={`Reason code ${i + 1}`} className="md:col-span-2"><input type="text" className="form-input" value={x.reason_code} onChange={setExclusion(i, 'reason_code')} /></Field>
                            <Field label={`Description ${i + 1}`} className="md:col-span-4"><input type="text" className="form-input" value={x.description} onChange={setExclusion(i, 'description')} /></Field>
                            <button type="button" className="btn btn-danger !px-3 md:col-span-1" aria-label={`Remove exclusion ${i + 1}`}
                                    onClick={() => setForm((f) => ({ ...f, exclusions: f.exclusions.filter((_, j) => j !== i) }))}>
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </button>
                        </div>
                    ))}
                    {errors.exclusions && <p className="text-[11px] text-danger font-semibold">{errors.exclusions}</p>}
                    <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={() => setForm((f) => ({ ...f, exclusions: [...f.exclusions, { ...EMPTY_EXCLUSION }] }))}>
                        Add Exclusion
                    </button>
                </fieldset>

                <ErrorNotice error={error} title="The draft was not saved" />
                <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Draft'}</button>
                    <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
                </div>
            </form>
        </Panel>
    );
}

function PolicyDetails({ policy, manufacturers, onChanged, onEdit, onCopy }) {
    const [auditVersion, setAuditVersion] = useState(0);
    const limit = (value, unit) => (value ? `${formatNumber(value)} ${unit}` : 'Not set');
    return (
        <Panel title={`Policy ${policy.policy_id}`} actions={<>
            {policy.status === 'DRAFT' && <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={onEdit}>Edit Draft</button>}
            <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={onCopy}>New Draft from This Policy</button>
        </>}>
            <div className="space-y-8">
                <DetailList items={[
                    ['Name', policy.policy_name],
                    ['Status', <StatusBadge status={policy.status} />],
                    ['Manufacturer', policy.manufacturer_code ? `${manufacturerName(manufacturers, policy.manufacturer_code)} (${policy.manufacturer_code})` : 'All manufacturers'],
                    ['Model scope', policy.model_code || 'All models of the manufacturer'],
                    ['Market', policy.market_code || 'All markets'],
                    ['Model years', policy.model_year_from || policy.model_year_to ? `${policy.model_year_from ?? '…'}–${policy.model_year_to ?? '…'}` : 'All'],
                    ['Coverage type', policy.coverage_type],
                    ['Warranty duration', policy.duration_months ? `${policy.duration_months} months` : 'Not set'],
                    ['Distance limit', limit(policy.distance_limit, policy.distance_unit)],
                    ['Limit rule', policy.limit_rule],
                    ['Effective', `${policy.effective_from} to ${policy.effective_to || 'open-ended'}`],
                    ['Priority', policy.priority],
                    ['Source', `${policy.source_type}${policy.source_reference ? ` — ${policy.source_reference}` : ''}`],
                ]} />
                <p className="text-xs text-text-muted">{policy.disclaimer}</p>
                <div>
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Covered components</h4>
                    {policy.coverage.length
                        ? <ul className="flex flex-wrap gap-2">{policy.coverage.map((c) => <li key={c} className="border border-line px-2 py-1 text-xs font-semibold">{c}</li>)}</ul>
                        : <p className="text-sm text-text-muted">No covered categories are configured.</p>}
                </div>
                <div>
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-2">Exclusions</h4>
                    {policy.exclusions.length ? (
                        <table className="min-w-full text-sm">
                            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-text-muted">
                                <th className="py-1 pr-4 font-semibold">Type</th><th className="py-1 pr-4 font-semibold">Value</th>
                                <th className="py-1 pr-4 font-semibold">Reason</th><th className="py-1 font-semibold">Description</th>
                            </tr></thead>
                            <tbody>{policy.exclusions.map((x) => (
                                <tr key={x.exclusion_id ?? `${x.match_type}-${x.match_value}`} className="border-t border-line">
                                    <td className="py-1 pr-4">{x.match_type}</td><td className="py-1 pr-4">{x.match_value}</td>
                                    <td className="py-1 pr-4"><code>{x.reason_code}</code></td><td className="py-1">{x.description}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    ) : <p className="text-sm text-text-muted">No exclusions are configured.</p>}
                </div>
                <div>
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-3">Lifecycle</h4>
                    <LifecycleActions kind="policy" id={policy.policy_id} status={policy.status}
                                      label={`${policy.policy_id} — ${manufacturerName(manufacturers, policy.manufacturer_code)}`}
                                      onChanged={(updated) => { setAuditVersion((v) => v + 1); onChanged(updated); }} />
                </div>
                <div>
                    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-3">Lifecycle audit</h4>
                    <LifecycleTimeline kind="policy" id={policy.policy_id} version={auditVersion} />
                </div>
            </div>
        </Panel>
    );
}

export default function WarrantyPolicies() {
    const { isAdmin, checked } = usePocAccess();
    const [status, setStatus] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(null);         // {mode, initial}
    const policies = useApiResource((signal) => (isAdmin ? listRecords('policy', { status }, signal) : Promise.resolve(null)), [status, isAdmin]);
    const manufacturers = useApiResource((signal) => (isAdmin ? listManufacturers(signal) : Promise.resolve([])), [isAdmin]);

    if (!checked) return <LoadingState label="Checking access…" />;
    if (!isAdmin) return <AccessDenied what="warranty policy administration" />;

    const names = manufacturers.data || [];
    const items = policies.data?.items || [];
    const selected = items.find((p) => p.policy_id === selectedId);
    const saved = (policy) => {
        setForm(null);
        setStatus('');
        setSelectedId(policy.policy_id);
        policies.reload();
    };

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <SubNav label="Warranty administration" links={ADMIN_LINKS} />
            <PageHeader title="Warranty Policies"
                        subtitle="Drafts can be edited, validated and activated. Active and retired policies are read-only: to change one, create a new draft, activate it and retire the previous policy."
                        actions={<>
                            <button type="button" className="btn btn-primary gap-2" onClick={() => setForm({ mode: 'create', initial: EMPTY_POLICY })}>
                                <Plus className="w-4 h-4" aria-hidden="true" />Create Draft Policy
                            </button>
                            <button type="button" className="btn btn-secondary gap-2" onClick={policies.reload} disabled={policies.loading}>
                                <RefreshCw className="w-4 h-4" aria-hidden="true" />Refresh
                            </button>
                        </>} />
            <Disclaimer />

            {form && <PolicyForm key={`${form.mode}-${form.initial.policy_id}`} mode={form.mode} initial={form.initial} manufacturers={names}
                                 onSaved={saved} onCancel={() => setForm(null)} />}

            <Panel title="Policies" actions={
                <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-muted">
                    Status
                    <select className="form-select !py-1 !px-2 !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
            }>
                {policies.loading && <LoadingState label="Loading policies…" />}
                <ErrorNotice error={policies.error} onRetry={policies.reload} />
                {!policies.loading && policies.data && (items.length ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <caption className="sr-only">Warranty policies</caption>
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-text-muted">
                                    {['Policy ID', 'Manufacturer', 'Model Scope', 'Coverage', 'Duration', 'Distance Limit', 'Effective', 'Priority', 'Source', 'Status', ''].map((h) => (
                                        <th key={h} scope="col" className="py-2 pr-4 font-semibold">{h || <span className="sr-only">Actions</span>}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.policy_id} className={`border-t border-line ${p.policy_id === selectedId ? 'bg-background' : ''}`}>
                                        <td className="py-2 pr-4 font-semibold whitespace-nowrap">{p.policy_id}</td>
                                        <td className="py-2 pr-4">{p.manufacturer_code ? manufacturerName(names, p.manufacturer_code) : 'All'}</td>
                                        <td className="py-2 pr-4">{p.model_code || 'All models'}</td>
                                        <td className="py-2 pr-4">{p.coverage_type}</td>
                                        <td className="py-2 pr-4 whitespace-nowrap">{p.duration_months ? `${p.duration_months} mo` : '—'}</td>
                                        <td className="py-2 pr-4 whitespace-nowrap">{p.distance_limit ? `${formatNumber(p.distance_limit)} ${p.distance_unit}` : '—'}</td>
                                        <td className="py-2 pr-4 whitespace-nowrap">{p.effective_from} – {p.effective_to || 'open'}</td>
                                        <td className="py-2 pr-4">{p.priority}</td>
                                        <td className="py-2 pr-4 text-xs">{p.source_type}</td>
                                        <td className="py-2 pr-4"><StatusBadge status={p.status} /></td>
                                        <td className="py-2">
                                            <button type="button" className="text-xs font-semibold uppercase tracking-widest underline underline-offset-2"
                                                    onClick={() => setSelectedId(p.policy_id)} aria-label={`View policy ${p.policy_id}`}>View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {policies.data.total > items.length && <p className="text-[11px] text-text-muted mt-3">Showing the first {items.length} of {policies.data.total} policies.</p>}
                    </div>
                ) : <EmptyState>No warranty policies match this filter.</EmptyState>)}
            </Panel>

            {selected && (
                <PolicyDetails policy={selected} manufacturers={names} onChanged={() => policies.reload()}
                               onEdit={() => setForm({ mode: 'edit', initial: policyToForm(selected, { keepId: true }) })}
                               onCopy={() => setForm({ mode: 'create', initial: policyToForm(selected, { keepId: false }) })} />
            )}
        </div>
    );
}
